import axios from 'axios';
import { FetchedUserEntitlementsDto } from 'Modules/AnytimeTicket/Components/UserEntitlements/UserEntitlementsUtils';
import { buildUrl } from 'Util/FetchUtils';
import { OverbookingWarningDto } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Reservation/OverbookingWarningDto';

// ---------------------------------------------------------------------------
// BlockedReason — strongly-typed discriminated union matching the API contract
// ---------------------------------------------------------------------------

export interface TicketExpiredReason {
	reasonCode: 'TICKET_EXPIRED';
	expiryDate: string;
}

export interface InsufficientBalanceReason {
	reasonCode: 'INSUFFICIENT_BALANCE';
	remainingUses: number;
	requestedUnits: number;
	/** null when the ticket was purchased but never used */
	lastUsedDate: string | null;
}

export interface NestedCapacityInfo {
	capacityTypeId: string;
	capacityTypeLabel: string;
	errorMessageId?: string;
}

export interface CapacityExceededReason {
	reasonCode: 'CAPACITY_EXCEEDED';
	currentCapacity: number;
	requestedUnits: number;
	overbookingWarnings: OverbookingWarningDto[];
}

export interface StopRange {
	startStopId: string;
	endStopId: string;
}

export interface PathRule {
	routeId: string;
	stopRange: StopRange | null;
}

export interface RouteRestrictionConfiguration {
	isEnabled: boolean;
	allowedPaths: PathRule[];
}

export interface RouteRestrictedReason {
	reasonCode: 'ROUTE_RESTRICTED';
	allowedRouteNames: string[];
	configuration: RouteRestrictionConfiguration;
	allowedSegmentNames?: string[];
}

export interface RouteNotFoundReason {
	reasonCode: 'ROUTE_NOT_FOUND';
	configuration: RouteRestrictionConfiguration;
}

export interface AuthorizationFailedReason {
	reasonCode: 'AUTHORIZATION_FAILED';
}

export interface UsageLimitExceededReason {
	reasonCode: 'USAGE_LIMIT_EXCEEDED';
	maxAllowedPerTrip: number;
	currentUsage: number;
	requestedUnits: number;
	passengerTypeName?: string;
}

export interface TicketLimitReason {
	reasonCode: 'TICKET_LIMIT';
	limit: number;
	currentCount: number;
}

export interface PackExhaustedReason {
	reasonCode: 'PACK_EXHAUSTED';
	packSize: number;
}

export interface InvalidTicketReason {
	reasonCode: 'INVALID_TICKET';
}

export interface SystemErrorReason {
	reasonCode: 'SYSTEM_ERROR';
	message: string;
}

export type BlockedReason =
	| TicketExpiredReason
	| InsufficientBalanceReason
	| CapacityExceededReason
	| RouteRestrictedReason
	| RouteNotFoundReason
	| AuthorizationFailedReason
	| TicketLimitReason
	| PackExhaustedReason
	| UsageLimitExceededReason
	| InvalidTicketReason
	| SystemErrorReason;

export interface AnytimePassengerSummary {
	passengerTypeName: string;
	quantity: number;
}

export interface AnytimeRedemptionPreview {
	productName?: string;
	description?: string;
	passengers: AnytimePassengerSummary[];
	cargoSummary?: string;
	expiryDate?: string;
	/** Uses left on the ticket before this redemption (shown as "X of Y"). */
	remainingUses?: number;
	/** Total uses the ticket was issued with. */
	initialUses?: number;
}

export interface BaseAnytimeRedemptionResponse {
	status: 'SUCCESS' | 'CONFLICT' | 'BLOCKED' | 'PREVIEW';
}

export interface AnytimeRedemptionSuccess extends BaseAnytimeRedemptionResponse {
	status: 'SUCCESS';
	bookingId: string;
}

export interface AnytimeRedemptionConflict extends BaseAnytimeRedemptionResponse {
	status: 'CONFLICT';
	conflictType: string;
	preview: AnytimeRedemptionPreview;
	reason?: BlockedReason;
	/** Optional server-supplied hint shown to staff in the confirmation UI */
	message?: string;
}

export interface AnytimeRedemptionBlocked extends BaseAnytimeRedemptionResponse {
	status: 'BLOCKED';
	reason: BlockedReason;
}

export interface AnytimeRedemptionPreviewResponse extends BaseAnytimeRedemptionResponse {
	status: 'PREVIEW';
	preview: AnytimeRedemptionPreview;
	/** Optional server-supplied hint shown to staff in the confirmation UI */
	message?: string;
}

export type AnytimeRedemptionResponse =
	| AnytimeRedemptionSuccess
	| AnytimeRedemptionConflict
	| AnytimeRedemptionBlocked
	| AnytimeRedemptionPreviewResponse;

export interface RedeemAnytimeTicketInput {
	ferryTripId: string;
	anytimeTicketId: string;
	stopId: string;
	overrideConflicts: boolean;
}

export const redeemAnytimeTicket = async (
	ferryTripId: string,
	anytimeTicketId: string,
	stopId: string,
	overrideConflicts: boolean = false,
): Promise<AnytimeRedemptionResponse> => {
	const url = '/api/AnytimeTicket/redeem';
	const body: RedeemAnytimeTicketInput = {
		ferryTripId,
		anytimeTicketId,
		stopId,
		overrideConflicts,
	};
	const response = await axios.post<AnytimeRedemptionResponse>(url, body);
	return response.data;
};

export interface InitiateTransactionResponse {
	transactionId: string;
	userEntitlementId: string | null;
	purchasedItemId: string | null;
}

export const initiateAnytimeTransaction = async (
	productId: string,
	productType: string = 'TICKET',
	customerId?: string | null,
	quantity?: number,
): Promise<InitiateTransactionResponse> => {
	const response = await axios.post<InitiateTransactionResponse>('/api/AnytimeTicket/initiate', {
		productId,
		productType,
		customerId,
		quantity,
	});
	return response.data;
};

export const abandonAnytimeTransaction = async (transactionId: string): Promise<void> => {
	await axios.post(`/api/AnytimeTicket/abandon?transactionId=${transactionId}`);
};

export const getUserEntitlements = async (
	userId: string,
	skip: number,
	take: number,
	showExhaustedOrExpired: boolean,
	search?: string,
): Promise<FetchedUserEntitlementsDto> => {
	const params: Record<string, string> = {
		userId,
		skip: skip.toString(),
		take: take.toString(),
		showInvalid: showExhaustedOrExpired ? 'true' : 'false',
	};
	if (search) {
		params.search = search;
	}
	const response = await axios.get(buildUrl(`/api/AnytimeTicket/user/${userId}`, params));
	return response.data;
};

export const updateUserEntitlementExpiry = async (id: string, expiryDate: string | null): Promise<void> => {
	await axios.patch(`/api/AnytimeTicket/${id}/expiry`, { expiryDate });
};

export const updateUserEntitlementUses = async (id: string, remainingUses: number): Promise<void> => {
	await axios.patch(`/api/AnytimeTicket/${id}/uses`, { remainingUses });
};

export interface RefundUserEntitlementResponse {
	entitlementId: string;
	refundedAmount: number;
	refundedAt: string;
}

export const refundUserEntitlement = async (id: string): Promise<RefundUserEntitlementResponse> => {
	const response = await axios.post<RefundUserEntitlementResponse>(`/api/AnytimeTicket/${id}/refund`);
	return response.data;
};

export interface TransferUserEntitlementResponse {
	entitlementId: string;
	transferId: string;
	fromUserId: string;
	toUserId: string;
	transferredAt: string;
}

export const transferUserEntitlement = async (
	id: string,
	toUserId: string,
	reason: string,
): Promise<TransferUserEntitlementResponse> => {
	const response = await axios.post<TransferUserEntitlementResponse>(`/api/AnytimeTicket/${id}/transfer`, {
		toUserId,
		reason,
	});
	return response.data;
};
